// client/src/lib/imageAnalysis.ts
export interface ImageAnalysisResult {
  status: 'healthy' | 'diseased' | 'pest_detected' | 'nutrient_deficiency';
  confidence: number;
  cropType: string;
  issues: string[];
  recommendations: string[];
  language: 'en' | 'st';
  provider?: string;
}

// Wait for TensorFlow.js to load
function waitForTensorFlow(): Promise<void> {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max
    
    const checkTf = () => {
      // @ts-ignore
      if (window.tf) {
        resolve();
        return;
      }
      
      attempts++;
      if (attempts >= maxAttempts) {
        reject(new Error('TensorFlow.js failed to load after 5 seconds'));
        return;
      }
      
      setTimeout(checkTf, 100);
    };
    
    checkTf();
  });
}

// Teachable Machine models for all crops
let loadedModels: { [key: string]: any } = {};

// Load Teachable Machine model
async function loadTeachableMachineModel(modelPath: string) {
  if (loadedModels[modelPath]) return loadedModels[modelPath];
  
  try {
    // @ts-ignore
    const tf = window.tf;
    if (!tf) {
      console.error('TensorFlow.js not available on window object');
      throw new Error('TensorFlow.js not loaded');
    }

    const model = await tf.loadLayersModel(modelPath);
    loadedModels[modelPath] = model;
    console.log(`✅ Teachable Machine model loaded: ${modelPath}`);
    return model;
  } catch (error) {
    console.error(`Failed to load model ${modelPath}:`, error);
    throw error;
  }
}

// Validate if image contains crop plants
function validateCropImage(imageElement: HTMLImageElement): { isValid: boolean; reason?: string } {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  canvas.width = 224;
  canvas.height = 224;
  ctx.drawImage(imageElement, 0, 0, 224, 224);
  
  const imageData = ctx.getImageData(0, 0, 224, 224);
  const data = imageData.data;
  
  let greenPixels = 0;
  let totalPixels = 0;
  let skinTonePixels = 0;
  let grayPixels = 0;
  
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    totalPixels++;
    
    // Check for green vegetation (plants)
    if (g > r && g > b && g > 80) {
      greenPixels++;
    }
    
    // Check for skin tones (people)
    if (r > 95 && g > 40 && b > 20 && r > b && (r - g) < 15) {
      skinTonePixels++;
    }
    
    // Check for grayscale (buildings, objects)
    if (Math.abs(r - g) < 15 && Math.abs(g - b) < 15 && Math.abs(r - b) < 15) {
      grayPixels++;
    }
  }
  
  const greenRatio = greenPixels / totalPixels;
  const skinRatio = skinTonePixels / totalPixels;
  const grayRatio = grayPixels / totalPixels;
  
  // Validation rules
  if (skinRatio > 0.15) {
    return { isValid: false, reason: 'This appears to be a photo of a person. Please upload a clear image of crop leaves.' };
  }
  
  if (grayRatio > 0.6) {
    return { isValid: false, reason: 'This appears to be a photo of a building or object. Please upload a clear image of crop leaves.' };
  }
  
  if (greenRatio < 0.1) {
    return { isValid: false, reason: 'No plant material detected. Please upload a clear image of crop leaves (corn, potato, tomato, pepper, or soybean).' };
  }
  
  return { isValid: true };
}

// Analyze with Teachable Machine models
async function analyzeWithTeachableMachine(imageFile: File, language: 'en' | 'st'): Promise<ImageAnalysisResult> {
  try {
    // Try corn model first (most important for Lesotho)
    const cornModel = await loadTeachableMachineModel('/models/plant-disease/model.json');
    
    // @ts-ignore
    const tf = window.tf;
    const img = new Image();
    
    return new Promise((resolve, reject) => {
      img.onload = async () => {
        try {
          // Validate image contains crop plants
          const validation = validateCropImage(img);
          if (!validation.isValid) {
            throw new Error(validation.reason || 'Invalid image for crop analysis');
          }
          
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d')!;
          canvas.width = 224;
          canvas.height = 224;
          ctx.drawImage(img, 0, 0, 224, 224);
          
          const tensor = tf.browser.fromPixels(canvas)
            .resizeNearestNeighbor([224, 224])
            .toFloat()
            .div(255.0)
            .expandDims();
          
          const predictions = await cornModel.predict(tensor).data();
          const maxIndex = predictions.indexOf(Math.max(...predictions));
          const confidence = predictions[maxIndex];
          
          // Load metadata to get actual class labels
          const metadataResponse = await fetch('/models/plant-disease/metadata.json');
          const metadata = await metadataResponse.json();
          const classLabels = metadata.labels;
          
          console.log('Loaded', classLabels.length, 'classes from Teachable Machine model');
          
          const prediction = classLabels[maxIndex] || 'Unknown';
          
          // Parse Teachable Machine format: "Crop_(type)___Disease_name"
          let cropType = 'Unknown';
          let condition = 'Unknown';
          
          if (prediction.includes('Corn_(maize)')) {
            cropType = 'Corn (Maize)';
            if (prediction.includes('healthy')) condition = 'Healthy';
            else if (prediction.includes('Cercospora')) condition = 'Cercospora Leaf Spot';
            else if (prediction.includes('Common_rust')) condition = 'Common Rust';
            else if (prediction.includes('Northern_Leaf_Blight')) condition = 'Northern Leaf Blight';
          } else if (prediction.includes('Potato')) {
            cropType = 'Potato';
            if (prediction.includes('healthy')) condition = 'Healthy';
            else if (prediction.includes('Early_blight')) condition = 'Early Blight';
            else if (prediction.includes('Late_blight')) condition = 'Late Blight';
          } else if (prediction.includes('Tomato')) {
            cropType = 'Tomato';
            if (prediction.includes('healthy')) condition = 'Healthy';
            else if (prediction.includes('Bacterial_spot')) condition = 'Bacterial Spot';
            else if (prediction.includes('Early_blight')) condition = 'Early Blight';
            else if (prediction.includes('Late_blight')) condition = 'Late Blight';
            else if (prediction.includes('Leaf_Mold')) condition = 'Leaf Mold';
            else if (prediction.includes('Septoria')) condition = 'Septoria Leaf Spot';
            else if (prediction.includes('Spider_mites')) condition = 'Spider Mites';
            else if (prediction.includes('Target_Spot')) condition = 'Target Spot';
            else if (prediction.includes('mosaic_virus')) condition = 'Mosaic Virus';
            else if (prediction.includes('Yellow_Leaf_Curl')) condition = 'Yellow Leaf Curl Virus';
          } else if (prediction.includes('Pepper')) {
            cropType = 'Pepper';
            if (prediction.includes('healthy')) condition = 'Healthy';
            else if (prediction.includes('Bacterial_spot')) condition = 'Bacterial Spot';
          } else if (prediction.includes('Soybean')) {
            cropType = 'Soybean';
            condition = 'Healthy';
          }
          
          tensor.dispose();
          
          resolve({
            status: condition?.toLowerCase().includes('healthy') ? 'healthy' : 'diseased',
            confidence: confidence,
            cropType: cropType,
            issues: condition?.toLowerCase().includes('healthy') ? [] : [condition],
            recommendations: generateRecommendations(cropType, condition, language),
            language,
            provider: 'Teachable Machine'
          });
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(imageFile);
    });
  } catch (error) {
    throw new Error(`Teachable Machine analysis failed: ${error}`);
  }
}

function generateRecommendations(cropType: string, condition: string, language: 'en' | 'st'): string[] {
  if (condition?.toLowerCase().includes('healthy')) {
    return language === 'en' ? [
      `Your ${cropType} appears healthy!`,
      'Continue current care practices',
      'Monitor regularly for early disease detection',
      'Maintain proper watering and fertilization'
    ] : [
      `${cropType} ea hau e bonahala e pholo!`,
      'Tsoela pele ka tlhokomelo ea hona joale',
      'Sheba kamehla bakeng sa ho bona mafu kapele',
      'Boloka nosetso le monontsha o nepahetseng'
    ];
  }
  
  // Disease-specific recommendations
  if (condition?.toLowerCase().includes('rust')) {
    return language === 'en' ? [
      `${condition} detected in ${cropType}`,
      'Apply fungicide containing propiconazole or tebuconazole',
      'Ensure good air circulation between plants',
      'Remove infected plant debris',
      'Plant resistant varieties next season'
    ] : [
      `${condition} e fumanoe ho ${cropType}`,
      'Sebelisa sebolaea-mafu se nang propiconazole',
      'Netefatsa phallo e ntle ea moea lijalong',
      'Tlosa matlapa a lijalo a kulang',
      'Lema mefuta e hanang mafu selemo se tlang'
    ];
  }
  
  if (condition?.toLowerCase().includes('blight')) {
    return language === 'en' ? [
      `${condition} detected in ${cropType}`,
      'Apply copper-based fungicide immediately',
      'Improve drainage to reduce moisture',
      'Remove and destroy infected leaves',
      'Avoid overhead irrigation'
    ] : [
      `${condition} e fumanoe ho ${cropType}`,
      'Sebelisa sebolaea-mafu sa koporo hang-hang',
      'Ntlafatsa phallo ea metsi ho fokotsa mongobo',
      'Tlosa le ho chesa makhasi a kulang',
      'Qoba ho nosetsa ka holimo'
    ];
  }
  
  // General disease recommendations
  return language === 'en' ? [
    `Disease detected: ${condition} in ${cropType}`,
    'Consult local agricultural extension office',
    'Remove affected plant parts',
    'Apply appropriate treatment',
    'Monitor other plants for spread'
  ] : [
    `Lefu le fumanoe: ${condition} ho ${cropType}`,
    'Buisana le ofisi ea temo ea lehae',
    'Tlosa likarolo tse amehileng tsa semela',
    'Sebelisa kalafi e nepahetseng',
    'Sheba lijalo tse ling bakeng sa ho ata'
  ];
}

export async function analyzeImage(imageFile: File, language: 'en' | 'st' = 'en'): Promise<ImageAnalysisResult> {
  try {
    // Wait for TensorFlow.js to load
    await waitForTensorFlow();
    
    // Try Teachable Machine models first (includes corn, potato, tomato)
    return await analyzeWithTeachableMachine(imageFile, language);
  } catch (error) {
    // Silently fallback to backend AI analysis when Teachable Machine fails
    
    // Fallback to backend AI analysis
    const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('language', language);

    const response = await fetch(`${backendUrl}/api/analyze-image`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Image analysis failed');
    }

    return response.json();
  }
}